"use client"

import { addPurchaseSelection, addToCart } from "@lib/data/cart"
import type { HttpTypes } from "@medusajs/types"
import { useCartDrawer } from "@modules/cart-drawer/context/cart-drawer-context"
import type { CartPendingAddPreview } from "@modules/cart-drawer/types"
import { trackMetaBusinessReceipt } from "@modules/meta/meta-event-client"
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { isConfiguredSinglePurchaseOption } from "../lib/bundle-option"
import type {
  PurchaseSelection,
  StoreBundle,
  StoreBundleComposition,
  StoreBundleOption,
} from "../types"

export type ProductPurchaseStatus = "ready" | "adding" | "error"

export type ProductPurchaseDisplayPrice = {
  current_amount: number
  compare_at_amount: number | null
  currency_code: string
}

export type ProductPurchaseAvailability = {
  available: boolean
  complete: boolean
  reason: string | null
}

export type ProductSelectedPurchase = {
  kind: "unconfigured" | "single" | "bundle"
  bundle_id: string | null
  option_id: string | null
  option_title: string
  badge_text: string | null
  highlight_text: string | null
  quantity: number
  variant_id: string | null
  display_price: ProductPurchaseDisplayPrice | null
  availability: ProductPurchaseAvailability
  can_purchase: boolean
}

type BundleSelection = Extract<PurchaseSelection, { kind: "bundle" }>

type ProductPurchaseContextValue = {
  selection: PurchaseSelection
  selectedPurchase: ProductSelectedPurchase
  selectionSummary: string
  displayPrice: ProductPurchaseDisplayPrice | null
  availability: ProductPurchaseAvailability
  canPurchase: boolean
  status: ProductPurchaseStatus
  error: string | null
  selectSingle(variantId: string): void
  selectBundle(selection: BundleSelection): void
  addCurrentSelection(): Promise<void>
}

const ProductPurchaseContext = createContext<ProductPurchaseContextValue | null>(null)

const isCompositionComplete = (
  option: StoreBundleOption,
  composition: StoreBundleComposition[]
) => {
  if (!composition.length) return false
  if (
    composition.some(
      (item) =>
        !item.product_id ||
        !item.variant_id ||
        !Number.isInteger(item.quantity) ||
        item.quantity <= 0
    )
  ) {
    return false
  }
  const total = composition.reduce((sum, item) => sum + item.quantity, 0)
  return option.quantity_mode === "minimum"
    ? total >= option.quantity
    : total === option.quantity
}

const variantAvailable = (variant: HttpTypes.StoreProductVariant) => {
  if (!variant.manage_inventory) return true
  if (variant.allow_backorder) return true
  return Number(variant.inventory_quantity ?? 0) > 0
}

const variantPrice = (
  variant: HttpTypes.StoreProductVariant | undefined
): ProductPurchaseDisplayPrice | null => {
  const calculated = variant?.calculated_price
  if (!calculated || calculated.calculated_amount == null) return null
  return {
    current_amount: Number(calculated.calculated_amount),
    compare_at_amount:
      calculated.original_amount == null
        ? null
        : Number(calculated.original_amount),
    currency_code: String(calculated.currency_code ?? "").toLowerCase(),
  }
}

const findBundleOption = (
  bundles: StoreBundle[],
  selection: BundleSelection
): { bundle: StoreBundle; option: StoreBundleOption } | null => {
  const bundle = bundles.find((candidate) => candidate.id === selection.bundle_id)
  const option = bundle?.options.find(
    (candidate) => candidate.id === selection.option_id
  )
  return bundle && option ? { bundle, option } : null
}

const optionIsPurchasable = (option: StoreBundleOption) =>
  option.available && (isConfiguredSinglePurchaseOption(option) || Boolean(option.pricing))

const hasAvailableBundleChoice = (bundles: StoreBundle[]) =>
  bundles.some(
    (bundle) =>
      bundle.available &&
      bundle.options.some(optionIsPurchasable)
  )

const findConfiguredSingleChoice = (bundles: StoreBundle[]) => {
  const availableBundles = bundles.filter((bundle) => bundle.available)
  for (const bundle of availableBundles) {
    const option = bundle.options.find(
      (candidate) =>
        candidate.id === bundle.default_option_id &&
        optionIsPurchasable(candidate) &&
        isConfiguredSinglePurchaseOption(candidate)
    )
    if (option) return { bundle, option }
  }
  for (const bundle of availableBundles) {
    const option = bundle.options.find(
      (candidate) => optionIsPurchasable(candidate) && isConfiguredSinglePurchaseOption(candidate)
    )
    if (option) return { bundle, option }
  }
  return null
}

const hasConfiguredSingleChoice = (bundles: StoreBundle[]) =>
  Boolean(findConfiguredSingleChoice(bundles))

const initialSingleVariantIdFor = (
  product: HttpTypes.StoreProduct,
  initialVariantId?: string
) => {
  const explicit = initialVariantId
    ? product.variants?.find((variant) => variant.id === initialVariantId)
    : undefined
  return (
    explicit ??
    product.variants?.find((variant) => variantAvailable(variant)) ??
    product.variants?.[0]
  )?.id ?? null
}

const initialSelectionFor = (
  product: HttpTypes.StoreProduct,
  bundles: StoreBundle[],
  initialVariantId?: string
): PurchaseSelection => {
  const explicitVariant = initialVariantId
    ? product.variants?.find((variant) => variant.id === initialVariantId)
    : undefined
  if (explicitVariant && (!bundles.length || hasConfiguredSingleChoice(bundles))) {
    return { kind: "single", variant_id: explicitVariant.id }
  }

  const defaultBundleSelection = bundles
    .filter((bundle) => bundle.available)
    .map((bundle) => {
      const option =
        bundle.options.find(
          (candidate) =>
            candidate.id === bundle.default_option_id &&
            optionIsPurchasable(candidate)
        ) ??
        bundle.options.find(
          (candidate) =>
            candidate.is_default && optionIsPurchasable(candidate)
        )

      if (!option) return null
      if (isConfiguredSinglePurchaseOption(option)) {
        const variantId = initialSingleVariantIdFor(product, initialVariantId)
        return variantId ? { kind: "single" as const, variant_id: variantId } : null
      }
      return {
        kind: "bundle" as const,
        bundle_id: bundle.id,
        option_id: option.id,
        composition: option.default_composition,
      }
    })
    .find((selection): selection is BundleSelection => Boolean(selection))

  if (defaultBundleSelection) {
    return defaultBundleSelection
  }

  const variants = product.variants ?? []
  const hasProductChoices = variants.length > 1 || (product.options?.length ?? 0) > 0
  if (!hasProductChoices && !hasAvailableBundleChoice(bundles) && variants.length === 1) {
    return { kind: "single", variant_id: variants[0].id }
  }

  return { kind: "unconfigured" }
}

export function ProductPurchaseProvider({
  product,
  bundles,
  countryCode,
  initialVariantId,
  children,
}: {
  product: HttpTypes.StoreProduct
  bundles: StoreBundle[]
  countryCode: string
  initialVariantId?: string
  children: ReactNode
}) {
  const [selection, setSelection] = useState<PurchaseSelection>(() =>
    initialSelectionFor(product, bundles, initialVariantId)
  )
  const [status, setStatus] = useState<ProductPurchaseStatus>("ready")
  const [error, setError] = useState<string | null>(null)
  const pendingRef = useRef(false)
  const {
    replaceSnapshot,
    openCart,
    closeCart,
    beginAdd,
    endAdd,
    refreshSnapshot,
  } = useCartDrawer()

  const derived = useMemo(() => {
    if (selection.kind === "unconfigured") {
      const previewVariant =
        product.variants?.find((candidate) => variantAvailable(candidate)) ??
        product.variants?.[0]
      return {
        selectionSummary: product.title ?? "Ürün",
        displayPrice: variantPrice(previewVariant),
        availability: {
          available: previewVariant ? variantAvailable(previewVariant) : false,
          complete: false,
          reason: "Satın alma seçimi tamamlanmadı.",
        } satisfies ProductPurchaseAvailability,
      }
    }

    if (selection.kind === "single") {
      const variant = product.variants?.find(
        (candidate) => candidate.id === selection.variant_id
      )
      const complete = Boolean(variant)
      const available = variant ? variantAvailable(variant) : false
      return {
        selectionSummary: variant?.title ?? product.title ?? "Ürün",
        displayPrice: variantPrice(variant),
        availability: {
          available,
          complete,
          reason: !complete
            ? "Varyant seçimi tamamlanmadı."
            : available
              ? null
              : "Bu seçenek şu anda stokta yok.",
        } satisfies ProductPurchaseAvailability,
      }
    }

    const resolved = findBundleOption(bundles, selection)
    if (!resolved) {
      return {
        selectionSummary: "Paket",
        displayPrice: null,
        availability: {
          available: false,
          complete: false,
          reason: "Paket seçimi bulunamadı.",
        } satisfies ProductPurchaseAvailability,
      }
    }

    const complete = isCompositionComplete(resolved.option, selection.composition)
    const available = Boolean(
      resolved.bundle.available && resolved.option.available && complete
    )
    const pricing = resolved.option.pricing
    return {
      selectionSummary: resolved.option.title,
      displayPrice: pricing
        ? {
            current_amount: pricing.expected_effective_total,
            compare_at_amount: pricing.compare_at_total,
            currency_code: String(
              product.variants?.[0]?.calculated_price?.currency_code ?? ""
            ).toLowerCase(),
          }
        : null,
      availability: {
        available,
        complete,
        reason: !complete
          ? "Paket içeriği tamamlanmadı."
          : !resolved.bundle.available || !resolved.option.available
            ? resolved.option.unavailable_reason ??
              resolved.bundle.unavailable_reason ??
              "Bu paket şu anda kullanılamıyor."
            : null,
      } satisfies ProductPurchaseAvailability,
    }
  }, [bundles, product, selection])

  const selectedPurchase = useMemo<ProductSelectedPurchase>(() => {
    if (selection.kind === "unconfigured") {
      return {
        kind: "unconfigured",
        bundle_id: null,
        option_id: null,
        option_title: product.title ?? "Ürün",
        badge_text: null,
        highlight_text: null,
        quantity: 0,
        variant_id: product.variants?.[0]?.id ?? null,
        display_price: derived.displayPrice,
        availability: derived.availability,
        can_purchase: false,
      }
    }

    if (selection.kind === "single") {
      const configured = findConfiguredSingleChoice(bundles)
      return {
        kind: "single",
        bundle_id: configured?.bundle.id ?? null,
        option_id: configured?.option.id ?? null,
        option_title: configured?.option.title ?? product.title ?? "Ürün",
        badge_text: configured?.option.badge_text ?? null,
        highlight_text: configured?.option.highlight_text ?? null,
        quantity: configured?.option.quantity ?? 1,
        variant_id: selection.variant_id || null,
        display_price: derived.displayPrice,
        availability: derived.availability,
        can_purchase:
          derived.availability.complete &&
          derived.availability.available &&
          Boolean(derived.displayPrice),
      }
    }

    const resolved = findBundleOption(bundles, selection)
    const option = resolved?.option
    const selectedVariantId =
      selection.composition.find((item) => item.product_id === product.id)?.variant_id ??
      product.variants?.[0]?.id ??
      null
    return {
      kind: "bundle",
      bundle_id: selection.bundle_id,
      option_id: selection.option_id,
      option_title: option?.title ?? "Paket",
      badge_text: option?.badge_text ?? null,
      highlight_text: option?.highlight_text ?? null,
      quantity: option?.quantity ?? selection.composition.reduce((sum, item) => sum + item.quantity, 0),
      variant_id: selectedVariantId,
      display_price: derived.displayPrice,
      availability: derived.availability,
      can_purchase:
        derived.availability.complete &&
        derived.availability.available &&
        Boolean(derived.displayPrice),
    }
  }, [bundles, derived, product, selection])

  const pendingPreview = useMemo<CartPendingAddPreview | null>(() => {
    const price = selectedPurchase.display_price
    if (!price || selection.kind === "unconfigured") return null

    const resolved = selection.kind === "bundle"
      ? findBundleOption(bundles, selection)
      : null
    const quantity = selection.kind === "bundle"
      ? selection.composition.reduce((sum, item) => sum + item.quantity, 0)
      : 1

    return {
      key: selection.kind === "bundle"
        ? `${selection.bundle_id}:${selection.option_id}`
        : selection.variant_id,
      title: String(
        (product.metadata as Record<string, unknown> | null)?.short_name ??
          product.title ??
          "Ürün"
      ),
      thumbnail: product.thumbnail ?? null,
      quantity,
      effectiveTotal: price.current_amount,
      compareAtTotal: price.compare_at_amount,
      currencyCode: price.currency_code,
      bundleLabel: resolved?.option.title ?? null,
    }
  }, [bundles, product, selectedPurchase.display_price, selection])

  const selectSingle = useCallback((variantId: string) => {
    setSelection({ kind: "single", variant_id: variantId })
    setStatus("ready")
    setError(null)
  }, [])

  const selectBundle = useCallback((next: BundleSelection) => {
    setSelection(next)
    setStatus("ready")
    setError(null)
  }, [])

  const canPurchase = selectedPurchase.can_purchase

  const addCurrentSelection = useCallback(async () => {
    if (pendingRef.current) return
    if (!canPurchase || selection.kind === "unconfigured") {
      setStatus("error")
      setError(selectedPurchase.availability.reason ?? "Satın alma seçimi tamamlanmadı.")
      return
    }

    if (!pendingPreview) {
      setStatus("error")
      setError("Sepet önizlemesi hazırlanamadı.")
      return
    }

    pendingRef.current = true
    setStatus("adding")
    setError(null)
    beginAdd(pendingPreview)
    openCart()
    try {
      const selectorConfigured = bundles.length > 0
      const snapshot =
        selection.kind === "single" && !selectorConfigured
          ? await addToCart({
              variantId: selection.variant_id,
              quantity: 1,
              countryCode,
            })
          : await addPurchaseSelection({
              countryCode,
              items: selection.kind === "single"
                ? [{
                    variant_id: selection.variant_id,
                    quantity: findConfiguredSingleChoice(bundles)?.option.quantity ?? 1,
                  }]
                : selection.composition.map((item) => ({
                    variant_id: item.variant_id,
                    quantity: item.quantity,
                  })),
            })
      replaceSnapshot(snapshot)
      if (snapshot.meta_tracking) trackMetaBusinessReceipt(snapshot.meta_tracking)
      if (selectorConfigured) void refreshSnapshot()
      setStatus("ready")
    } catch {
      closeCart()
      setStatus("error")
      setError("Sepete eklenemedi. Lütfen tekrar deneyin.")
    } finally {
      endAdd()
      pendingRef.current = false
    }
  }, [
    beginAdd,
    canPurchase,
    closeCart,
    countryCode,
    selectedPurchase.availability.reason,
    endAdd,
    openCart,
    replaceSnapshot,
    refreshSnapshot,
    selection,
    pendingPreview,
    bundles,
  ])

  const value = useMemo<ProductPurchaseContextValue>(
    () => ({
      selection,
      selectedPurchase,
      selectionSummary: selectedPurchase.option_title,
      displayPrice: selectedPurchase.display_price,
      availability: selectedPurchase.availability,
      canPurchase,
      status,
      error,
      selectSingle,
      selectBundle,
      addCurrentSelection,
    }),
    [selection, selectedPurchase, canPurchase, status, error, selectSingle, selectBundle, addCurrentSelection]
  )

  return (
    <ProductPurchaseContext.Provider value={value}>
      {children}
    </ProductPurchaseContext.Provider>
  )
}

export function useProductPurchase() {
  const value = useContext(ProductPurchaseContext)
  if (!value) {
    throw new Error("useProductPurchase must be used within ProductPurchaseProvider")
  }
  return value
}
